import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { useOnboardingJourney } from '../../Data/Onboarding';
import { useSession } from '../Session';
import Clarity from '@microsoft/clarity';
import { Roles } from '../../Data/Users';
import { captureException } from '@sentry/react';
import trackActivity from './trackActivity';
import { isProductionApp } from '../../Services/environment';

// The GA property and Clarity project are the live ones, so only the real
// deployment may report to them. Leaving the SDKs uninitialised elsewhere is not
// enough on its own — calling into them afterwards throws — so every send site
// below is guarded too.
const reporting = isProductionApp();

if (reporting) {
    try {
        ReactGA.initialize('G-1340C5LCEF');
        Clarity.init("qkhqbf7wbv");

        window.clarity('consentv2', {
            ad_Storage: "granted",
            analytics_Storage: "granted",
        });
    } catch (err) {
        captureException(err);
    }
}

const Analytics = () => {
    const {user, business, plan, hasToken, adminLevel, isRoleSufficient, experiments} = useSession();
    const location = useLocation();
    const {journey} = useOnboardingJourney(false, !hasToken || !isRoleSufficient(Roles.MANAGER));

    useEffect(() => {
        if (!reporting) {
            return;
        }

        try {
            ReactGA.send({
                hitType: "pageview",
                page: location.pathname + location.search,
                title: window.document.title,
            });
        } catch (err) {
            captureException(err);
        }
    }, [location.pathname, location.search]);

    useEffect(() => {
        if (!reporting || !journey) {
            return;
        }

        try {
            const progress = journey?.progress?.percent ?? null;
            if (progress) {
                ReactGA.gtag('set','user_properties', {
                    onboarding_progress: progress,
                });
            }

            const activity = journey?.activity?.score ?? null;
            if (activity) {
                ReactGA.gtag('set','user_properties', {
                    onboarding_activity: activity,
                });
            }
        } catch (err) {
            captureException(err);
        }
    }, [journey]);

    useEffect(() => {
        const groups = Object.entries(experiments ?? {}).map(([name, group]) => `${name}:${group}`).join(',');

        if (!reporting || !groups) {
            return;
        }

        try {
            ReactGA.gtag('set', 'user_properties', {experiments: groups});
        } catch (err) {
            captureException(err);
        }
    }, [experiments]);

    useEffect(() => {
        if (!reporting || !user || !business || !plan) {
            return;
        }

        try {
            Clarity.identify(user.id);

            ReactGA.gtag('set','user_properties', {
                plan: plan.name,
                businessId: business.id,
                userId: user.id,
                adminLevel,
            });
        } catch (err) {
            captureException(err);
        }
    }, [user, business, plan, adminLevel]);

    return null;
}

export default Analytics;

export { trackActivity };
